import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { rankAutocompleteOptions } from '../lib/masterDataSearch';
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover';

export const MasterDataAutocomplete = ({
  value,
  onValueChange,
  options = [],
  placeholder = 'Select from Master Data...',
  searchPlaceholder = 'Type to search...',
  emptyMessage = 'No matching Master Data record',
  disabled = false,
  className,
  inputClassName,
  testId,
  tabIndex,
  onFocus,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(
    () => rankAutocompleteOptions(options, query),
    [options, query],
  );

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
  }, [open, query]);

  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const openSearch = () => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const selectOption = (option) => {
    onValueChange(option.value, option);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openSearch();
      else if (filteredOptions.length) setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) openSearch();
      else setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      const option = filteredOptions[activeIndex];
      if (option) selectOption(option);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const handleOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div className={cn('relative min-w-0', className)}>
          <Search className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            id={testId}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            aria-controls={`${testId}-listbox`}
            aria-activedescendant={open && filteredOptions[activeIndex] ? `${testId}-option-${activeIndex}` : undefined}
            value={open ? query : selectedOption?.label || ''}
            placeholder={open ? searchPlaceholder : placeholder}
            disabled={disabled}
            tabIndex={tabIndex}
            onFocus={(event) => {
              openSearch();
              onFocus?.(event);
            }}
            onClick={() => {
              if (!open) openSearch();
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onBlur={(event) => onBlur?.(event)}
            className={cn(
              'flex h-10 w-full min-w-0 rounded-md border border-input bg-background pl-9 pr-9 py-2 text-sm text-foreground shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground focus:border-maroon-500 focus:ring-1 focus:ring-maroon-500 disabled:cursor-not-allowed disabled:opacity-50',
              inputClassName,
            )}
            data-testid={testId}
          />
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              openSearch();
              inputRef.current?.focus();
            }}
            className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-gray-500 hover:text-maroon-700 disabled:opacity-50"
            aria-label={`Open ${placeholder}`}
            data-testid={`${testId}-toggle`}
          >
            <ChevronsUpDown className="h-4 w-4" />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        style={{ width: inputRef.current?.getBoundingClientRect().width }}
        className="min-w-[min(20rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] p-0 overflow-hidden bg-white"
      >
        <div
          id={`${testId}-listbox`}
          role="listbox"
          className="max-h-64 overflow-y-auto overflow-x-hidden p-1"
          data-testid={`${testId}-listbox`}
        >
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-gray-500" data-testid={`${testId}-empty`}>
              {emptyMessage}
            </div>
          ) : filteredOptions.map((option, index) => (
            <button
              key={`${option.value}-${index}`}
              id={`${testId}-option-${index}`}
              ref={(element) => { optionRefs.current[index] = element; }}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectOption(option)}
              className={cn(
                'flex min-h-[44px] w-full min-w-0 items-center gap-2 rounded px-3 py-2 text-left text-sm transition-colors',
                index === activeIndex ? 'bg-maroon-50 text-maroon-950' : 'hover:bg-gray-50',
              )}
              data-testid={option.testId || `${testId}-option-${index}`}
            >
              <Check className={cn('h-4 w-4 shrink-0', option.value === value ? 'opacity-100 text-maroon-700' : 'opacity-0')} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{option.label}</span>
                {option.description && <span className="block truncate text-xs text-gray-500">{option.description}</span>}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};